import React from 'react';
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { EventLogEntry, ExistingCredential } from '../types';

const METHOD_COLORS: Record<string, string> = {
  GET:    '#f57c00',
  POST:   '#1565c0',
  PUT:    '#6a1b9a',
  PATCH:  '#546e7a',
  DELETE: '#c62828',
};

const STATUS_CODE_COLORS: Record<string, string> = {
  '2': '#2e7d32',
  '3': '#0277bd',
  '4': '#e65100',
  '5': '#c62828',
};

const STATUS_LEVEL_MAP: Record<string, { label: string; bg: string; color: string }> = {
  '2': { label: 'Success', bg: '#e8f5e9', color: '#2e7d32' },
  '3': { label: 'Info',    bg: '#e8f4fd', color: '#1565c0' },
  '4': { label: 'Warning', bg: '#fff3e0', color: '#e65100' },
  '5': { label: 'Error',   bg: '#fdecea', color: '#c62828' },
};

const getStatusLevel = (status: number) =>
  STATUS_LEVEL_MAP[String(status).charAt(0)] ?? { label: 'Info', bg: '#e8f4fd', color: '#1565c0' };

const getStatusCodeColor = (status: number) =>
  STATUS_CODE_COLORS[String(status).charAt(0)] ?? '#555';

import moment from 'moment';
import { OPERATION_LABELS } from './EventLogFilterPanel';

const formatTimestamp = (ts: string): string => {
  const m = moment(ts);
  return m.isValid() ? m.local().format('MMM D, YYYY h:mm:ss A') : ts;
};

const parseQueryString = (qs: string): Array<{ key: string; value: string }> => {
  const clean = qs.startsWith('?') ? qs.slice(1) : qs;
  try {
    return Array.from(new URLSearchParams(clean).entries()).map(([key, value]) => ({ key, value }));
  } catch {
    return [];
  }
};

const formatBody = (raw: string): string => {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

interface EventLogDetailRowProps {
  log: EventLogEntry;
  credential?: ExistingCredential;
  isExpanded: boolean;
  onToggle: () => void;
}

const EventLogDetailRow = ({ log, credential, isExpanded, onToggle }: EventLogDetailRowProps) => {
  const hasDetails = !!(log.queryString || log.requestBody || log.responseBody);
  const queryParams = log.queryString ? parseQueryString(log.queryString) : [];
  const bodyText = log.requestBody ? formatBody(log.requestBody) : null;
  const responseBodyText = log.responseBody ? formatBody(log.responseBody) : null;

  return (
    <React.Fragment>
      {/* ── Main row ── */}
      <TableRow
        hover
        onClick={() => hasDetails && onToggle()}
        sx={{
          cursor: hasDetails ? 'pointer' : 'default',
          bgcolor: isExpanded ? 'action.selected' : undefined,
          '& > td': { borderBottom: isExpanded ? 'none' : undefined },
        }}
      >
        {/* Chevron */}
        <TableCell sx={{ width: 40, color: 'text.secondary', p: '4px 8px' }}>
          {hasDetails && (
            <IconButton
              size="small"
              aria-expanded={isExpanded}
              aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
              onClick={e => {
                e.stopPropagation();
                onToggle();
              }}
            >
              <ExpandMoreIcon
                fontSize="small"
                sx={{
                  transition: 'transform 0.2s',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </IconButton>
          )}
        </TableCell>

        {/* Level badge */}
        <TableCell sx={{ p: '4px 8px' }}>
          {(() => {
            const level = getStatusLevel(log.responseStatus);
            return (
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  px: 1,
                  py: 0.25,
                  borderRadius: '12px',
                  bgcolor: level.bg,
                  color: level.color,
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
              >
                {level.label}
              </Box>
            );
          })()}
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.82rem', color: 'text.secondary' }}>
          {formatTimestamp(log.timestamp)}
        </TableCell>

        <TableCell>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, fontSize: '0.82rem', color: 'text.primary' }}
          >
            {OPERATION_LABELS[log.operationId] ?? log.operationId}
          </Typography>
        </TableCell>

        <TableCell>
          <Typography
            component="span"
            sx={{
              fontWeight: 700,
              fontFamily: 'monospace',
              fontSize: '0.78rem',
              color: METHOD_COLORS[log.method] ?? '#546e7a',
            }}
          >
            {log.method}
          </Typography>
        </TableCell>

        <TableCell>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.78rem',
              maxWidth: 260,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={log.path}
          >
            {log.path}
          </Typography>
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          <Typography
            component="span"
            sx={{
              fontWeight: 700,
              fontFamily: 'monospace',
              fontSize: '0.88rem',
              color: getStatusCodeColor(log.responseStatus),
            }}
          >
            {log.responseStatus}
          </Typography>
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '0.82rem' }}>
          {log.durationMs}ms
        </TableCell>

        <TableCell sx={{ fontSize: '0.82rem' }}>
          {credential ? (credential.description || credential.key) : log.thirdPartyId?.substring(0, 8)}
        </TableCell>

        <TableCell>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
            {log.clientIp}
          </Typography>
        </TableCell>
      </TableRow>

      {/* ── Expanded detail row ── */}
      <TableRow sx={{ bgcolor: 'grey.50' }}>
        <TableCell colSpan={10} sx={{ py: 0, border: isExpanded ? undefined : 'none' }}>
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ px: 3, py: 2, display: 'flex', gap: 4, flexWrap: 'wrap' }}>

              {/* Trace info */}
              <Box sx={{ minWidth: 220 }}>
                <Typography
                  variant="overline"
                  sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 1 }}
                >
                  Details
                </Typography>
                <Table size="small" sx={{ width: 'auto' }}>
                  <TableBody>
                    {[
                      ['Trace ID', log.traceId],
                      ['Account ID', log.accountId],
                    ].map(([label, val]) =>
                      val ? (
                        <TableRow key={label} sx={{ '&:last-child td': { border: 0 } }}>
                          <TableCell
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.78rem',
                              fontWeight: 600,
                              color: 'primary.main',
                              pr: 2,
                              py: 0.5,
                              border: 'none',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {label}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.78rem',
                              color: 'text.primary',
                              py: 0.5,
                              border: 'none',
                            }}
                          >
                            {val}
                          </TableCell>
                        </TableRow>
                      ) : null
                    )}
                  </TableBody>
                </Table>

                {/* Response Body */}
                {responseBodyText && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="overline"
                      sx={{
                        fontWeight: 600,
                        color: log.responseStatus != null && log.responseStatus >= 400 ? 'error.main' : 'text.secondary',
                        display: 'block',
                        mb: 0.5,
                      }}
                    >
                      Response Body
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1.5,
                        bgcolor: log.responseStatus != null && log.responseStatus >= 400 ? '#fdecea' : 'grey.100',
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '0.78rem',
                        overflowX: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: 320,
                        overflowY: 'auto',
                        border: '1px solid',
                        borderColor: log.responseStatus != null && log.responseStatus >= 400 ? '#f44336' : 'divider',
                      }}
                    >
                      {responseBodyText}
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Query Parameters */}
              {queryParams.length > 0 && (
                <>
                  <Divider orientation="vertical" flexItem />
                  <Box sx={{ minWidth: 240 }}>
                    <Typography
                      variant="overline"
                      sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 1 }}
                    >
                      Query Parameters
                    </Typography>
                    <Table size="small" sx={{ width: 'auto' }}>
                      <TableBody>
                        {queryParams.map(({ key, value }) => (
                          <TableRow key={key} sx={{ '&:last-child td': { border: 0 } }}>
                            <TableCell
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.78rem',
                                fontWeight: 600,
                                color: 'primary.main',
                                pr: 2,
                                py: 0.5,
                                border: 'none',
                              }}
                            >
                              {key}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.78rem',
                                color: 'text.primary',
                                py: 0.5,
                                border: 'none',
                              }}
                            >
                              {value}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </>
              )}

              {/* Request Body */}
              {bodyText && (
                <>
                  <Divider orientation="vertical" flexItem />
                  <Box sx={{ flex: 1, minWidth: 280 }}>
                    <Typography
                      variant="overline"
                      sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 1 }}
                    >
                      Request Body
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1.5,
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '0.78rem',
                        overflowX: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxHeight: 320,
                        overflowY: 'auto',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      {bodyText}
                    </Box>
                  </Box>
                </>
              )}

            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

export default EventLogDetailRow;
